import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartPulse,
  faCircleCheck,
  faCircleXmark,
  faExclamationCircle,
  faRotateRight,
  faTriangleExclamation,
  faClock,
  faServer,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { toast } from '@/lib/swal';
import { getOverallHealth, getProviders, getAlerts, checkProvider } from './api/ame-platform.api';
import type { HealthStatusDto, ProviderHealthDto, AlertDto } from './api/ame-platform.api';

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.max(bytes, 1)) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

const HEALTH_CONFIG = {
  healthy: {
    icon: faCircleCheck,
    color: 'text-green-600 dark:text-green-400',
    label: 'Healthy',
    bg: 'bg-green-500/10',
  },
  degraded: {
    icon: faExclamationCircle,
    color: 'text-amber-500',
    label: 'Degraded',
    bg: 'bg-amber-500/10',
  },
  offline: {
    icon: faCircleXmark,
    color: 'text-destructive',
    label: 'Offline',
    bg: 'bg-destructive/10',
  },
};

const CONN_CONFIG = {
  HEALTHY: { color: 'text-green-600 dark:text-green-400', label: 'Healthy', icon: faCircleCheck },
  DEGRADED: { color: 'text-amber-500', label: 'Degraded', icon: faExclamationCircle },
  OFFLINE: { color: 'text-destructive', label: 'Offline', icon: faCircleXmark },
};

function getConnCfg(status: string) {
  return CONN_CONFIG[
    (status as keyof typeof CONN_CONFIG) in CONN_CONFIG
      ? (status as keyof typeof CONN_CONFIG)
      : 'OFFLINE'
  ];
}

const ALERT_STYLES = {
  critical: {
    border: 'border-l-destructive',
    bg: 'bg-destructive/5',
    text: 'text-destructive',
    label: 'CRITICAL',
  },
  warning: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-500/5',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'WARNING',
  },
  info: { border: 'border-l-primary', bg: 'bg-primary/5', text: 'text-primary', label: 'INFO' },
};

export function StorageHealthPage() {
  const [health, setHealth] = useState<HealthStatusDto | null>(null);
  const [providers, setProviders] = useState<ProviderHealthDto[]>([]);
  const [alerts, setAlerts] = useState<AlertDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([getOverallHealth(), getProviders(), getAlerts()])
      .then(([h, p, a]) => {
        setHealth(h);
        setProviders(p);
        setAlerts(a);
      })
      .catch(() => toast.error('Failed to load health data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCheck = async (id: string, name: string) => {
    setChecking(id);
    try {
      const result = await checkProvider(id);
      if (result.success) {
        toast.success(`${name} — healthy (${result.latencyMs}ms)`);
      } else {
        toast.error(`${name} — ${result.error ?? 'failed'}`);
      }
      load();
    } catch {
      toast.error('Health check failed');
    } finally {
      setChecking(null);
    }
  };

  const overallCfg = health
    ? (HEALTH_CONFIG[health.status] ?? HEALTH_CONFIG.offline)
    : HEALTH_CONFIG.offline;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Storage Health</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Provider connectivity, alerts, and live health checks
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className={cn(
            'flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
            loading && 'opacity-50 cursor-not-allowed',
          )}
        >
          <FontAwesomeIcon
            icon={faRotateRight}
            className={cn('h-3.5 w-3.5', loading && 'animate-spin')}
          />
          Refresh
        </button>
      </div>

      {/* Overall Health Banner */}
      <div
        className={cn(
          'flex items-center gap-4 rounded-xl border border-border p-5',
          loading ? 'bg-muted/30' : overallCfg.bg,
        )}
      >
        {loading ? (
          <div className="h-10 w-10 animate-pulse rounded-lg bg-muted" />
        ) : (
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
              overallCfg.bg,
            )}
          >
            <FontAwesomeIcon icon={overallCfg.icon} className={cn('h-5 w-5', overallCfg.color)} />
          </div>
        )}
        <div>
          <p className="text-xs font-medium text-muted-foreground">Overall Storage Status</p>
          {loading ? (
            <div className="mt-1 h-6 w-24 animate-pulse rounded bg-muted" />
          ) : (
            <p className={cn('text-lg font-bold', overallCfg.color)}>{overallCfg.label}</p>
          )}
          {health?.checkedAt && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
              Checked at {new Date(health.checkedAt).toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {/* Disk Stats */}
      {!loading && health?.diskStats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Used Space', value: formatBytes(health.diskStats.usedSpaceBytes) },
            { label: 'Available Space', value: formatBytes(health.diskStats.availableSpaceBytes) },
            { label: 'Total Space', value: formatBytes(health.diskStats.totalSpaceBytes) },
            {
              label: 'Usage',
              value:
                health.diskStats.usagePercent !== null ? `${health.diskStats.usagePercent}%` : '—',
            },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Providers */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold text-foreground">Storage Providers</h2>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : providers.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <FontAwesomeIcon icon={faServer} className="h-6 w-6 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No providers registered</p>
            </div>
          ) : (
            <div className="space-y-3">
              {providers.map((p) => {
                const cfg = getConnCfg(p.connectionStatus);
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-lg border border-border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon icon={faServer} className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-foreground">{p.name}</span>
                          {p.isActive && (
                            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
                              ACTIVE
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <FontAwesomeIcon icon={cfg.icon} className={cn('h-3 w-3', cfg.color)} />
                          <span className={cn('text-xs', cfg.color)}>{cfg.label}</span>
                          {p.lastHealthCheck && (
                            <span className="text-xs text-muted-foreground ml-1">
                              · {new Date(p.lastHealthCheck).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => void handleCheck(p.id, p.name)}
                      disabled={checking === p.id}
                      className={cn(
                        'rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted',
                        checking === p.id && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      {checking === p.id ? '…' : 'Check'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold text-foreground">
            Active Alerts
            {alerts.length > 0 && (
              <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-bold text-destructive">
                {alerts.length}
              </span>
            )}
          </h2>
          {loading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <FontAwesomeIcon
                icon={faHeartPulse}
                className="h-6 w-6 text-green-500 dark:text-green-400 mb-2"
              />
              <p className="text-sm font-medium text-foreground">No active alerts</p>
              <p className="mt-1 text-xs text-muted-foreground">
                All storage providers are operating normally
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert, i) => {
                const cfg = ALERT_STYLES[alert.severity] ?? ALERT_STYLES.info;
                return (
                  <div
                    key={i}
                    className={cn('rounded-lg border-l-4 px-3 py-2.5', cfg.border, cfg.bg)}
                  >
                    <div className="flex items-start gap-2">
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', cfg.text)}
                      />
                      <div>
                        <p className={cn('text-[10px] font-bold', cfg.text)}>{cfg.label}</p>
                        <p className={cn('text-xs', cfg.text)}>{alert.message}</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {new Date(alert.detectedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
